import { 
  ChartPieIcon, 
  QuestionMarkCircleIcon, 
  Cog6ToothIcon,
  BoltIcon, 
  ArchiveBoxIcon, 
  DocumentChartBarIcon, 
  WrenchScrewdriverIcon,
  PresentationChartLineIcon, 
  CpuChipIcon, 
  AdjustmentsHorizontalIcon, 
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

export interface FeatureFlagRoute {
  path: string;
  label: string;
  featureKey: string;
  icon: React.ElementType;
  category?: string;
  order?: number;
  showInSidebar?: boolean; // NEW: Control sidebar visibility (defaults to true)
}

/**
 * Maps route paths to feature flag keys and display metadata.
 * This replaces routeConfig.ts as the source of truth for navigation.
 * 
 * NOTE: This is the new feature flag-based routing system.
 * Legacy routeConfig.ts will be deprecated after migration is complete.
 */
export const featureFlagRoutes: FeatureFlagRoute[] = [
  // Core Features (order: 1-99)
  {
    path: '/dashboard',
    label: 'Dashboard',
    featureKey: 'dashboard',
    icon: ChartPieIcon,
    category: 'core',
    order: 1
  },
  
  // Trading Features (order: 100-199)
  {
    path: '/live-trades',
    label: 'Live Trades',
    featureKey: 'live_trades',
    icon: BoltIcon,
    category: 'trading',
    order: 100
  },
  {
    path: '/closed-trades',
    label: 'Closed Trades',
    featureKey: 'closed_trades',
    icon: ArchiveBoxIcon,
    category: 'trading',
    order: 101
  },
  
  // Analytics Features (order: 200-299)
  {
    path: '/strategy-details',
    label: 'Strategy Details',
    featureKey: 'strategy_details',
    icon: DocumentChartBarIcon,
    category: 'analytics',
    order: 200
  },
  {
    path: '/forecast',
    label: 'Forecast',
    featureKey: 'portfolio_forecast',
    icon: PresentationChartLineIcon,
    category: 'analytics',
    order: 201
  },
  
  // Premium Features (order: 300-399)
  {
    path: '/portfolio-optimization',
    label: 'Optimization',
    featureKey: 'portfolio_optimization',
    icon: AdjustmentsHorizontalIcon,
    category: 'premium',
    order: 300
  },
  {
    path: '/portfolio-command',
    label: 'Command Center',
    featureKey: 'portfolio_command_center',
    icon: CpuChipIcon,
    category: 'premium',
    order: 301
  },
  // Note: /portfolio-command-v2 uses same feature flag as /portfolio-command
  // Hidden from sidebar - alternative version, not a separate menu item
  {
    path: '/portfolio-command-v2',
    label: 'Command Center v2',
    featureKey: 'portfolio_command_center', // Reuses same feature flag
    icon: CpuChipIcon,
    category: 'premium',
    order: 302,
    showInSidebar: false // Hidden: Alternative route, not a menu item
  },
  
  // Management Features (order: 400-499)
  {
    path: '/bot-maintenance',
    label: 'Bot Maintenance',
    featureKey: 'bot_maintenance',
    icon: WrenchScrewdriverIcon,
    category: 'management',
    order: 400
  },
  
  // Account Features (order: 800-899)
  {
    path: '/settings',
    label: 'Settings',
    featureKey: 'account_settings',
    icon: Cog6ToothIcon,
    category: 'account',
    order: 800
  },
  // Email Preferences - Hidden from sidebar (accessed via Settings submenu)
  {
    path: '/email-preferences',
    label: 'Email Preferences',
    featureKey: 'email_preferences',
    icon: Cog6ToothIcon,
    category: 'account',
    order: 801,
    showInSidebar: false // Hidden: Accessed via Settings page
  },
  // Privacy & Data - Hidden from sidebar (accessed via Settings submenu)
  {
    path: '/privacy',
    label: 'Privacy & Data',
    featureKey: 'privacy_settings',
    icon: Cog6ToothIcon,
    category: 'account',
    order: 802,
    showInSidebar: false // Hidden: Accessed via Settings page
  },
  
  // Support Features (order: 900-999)
  {
    path: '/faq',
    label: 'Help',
    featureKey: 'help_faq',
    icon: QuestionMarkCircleIcon,
    category: 'support',
    order: 900
  },
  
  // Admin Features (order: 1000+)
  {
    path: '/admin',
    label: 'Admin Panel',
    featureKey: 'admin_panel',
    icon: ShieldCheckIcon,
    category: 'admin',
    order: 1000
  },
  // RT Diagnostic - Hidden from sidebar (admin tool, accessed directly)
  {
    path: '/real-time-diagnostic',
    label: 'RT Diagnostic',
    featureKey: 'real_time_diagnostic',
    icon: ShieldCheckIcon,
    category: 'admin',
    order: 1001,
    showInSidebar: false // Hidden: Admin diagnostic tool, not a primary nav item
  },
  // Lifecycle Testing - Hidden from sidebar (admin tool, accessed directly)
  {
    path: '/lifecycle-testing',
    label: 'Lifecycle Testing',
    featureKey: 'lifecycle_testing',
    icon: ShieldCheckIcon,
    category: 'admin',
    order: 1002,
    showInSidebar: false // Hidden: Admin testing tool, not a primary nav item
  }
];

/**
 * Helper: Get feature key for a given route path
 * Handles exact matches and prefix matches (for dynamic routes like /live-trades/chart/:id)
 */
export const getFeatureKeyForPath = (path: string): string | undefined => {
  // First try exact match
  let route = featureFlagRoutes.find(r => r.path === path);
  
  // If no exact match, try prefix match (for dynamic routes)
  if (!route) {
    route = featureFlagRoutes.find(r => path.startsWith(r.path));
  }
  
  return route?.featureKey;
};

/**
 * Helper: Get route config by feature key
 */
export const getRouteByFeatureKey = (featureKey: string): FeatureFlagRoute | undefined => {
  return featureFlagRoutes.find(r => r.featureKey === featureKey);
};

/**
 * Helper: Get all routes sorted by order
 * @param sidebarOnly - If true, only return routes visible in sidebar (default: false)
 */
export const getSortedRoutes = (sidebarOnly: boolean = false): FeatureFlagRoute[] => {
  let routes = [...featureFlagRoutes];
  
  // Filter to sidebar-visible routes if requested
  if (sidebarOnly) {
    routes = routes.filter(r => r.showInSidebar !== false); // Default to true if undefined
  }
  
  return routes.sort((a, b) => (a.order || 0) - (b.order || 0));
};

/**
 * Helper: Get routes by category
 */
export const getRoutesByCategory = (category: string): FeatureFlagRoute[] => {
  return featureFlagRoutes.filter(r => r.category === category);
};

/**
 * Helper: Get all unique categories
 */
export const getCategories = (): string[] => {
  const categories = new Set(featureFlagRoutes.map(r => r.category).filter(Boolean));
  return Array.from(categories) as string[];
};

/**
 * Build dynamic routes from feature flags API response
 * This replaces hardcoded routes with database-driven configuration
 * 
 * @param featureFlags - Feature flags object from useFeatureFlags hook
 * @returns Array of FeatureFlagRoute objects built from database metadata
 */
import { getIconForFeature } from './featureIconMapping';

interface FeatureFlagData {
  enabled: boolean;
  display_name: string;
  description?: string;
  feature_type: string;
  category?: string;
  metadata?: {
    sidebar_visible?: boolean;
    display_order?: number;
    route_path?: string;
  };
}

export const buildRoutesFromFeatureFlags = (
  featureFlags: Record<string, FeatureFlagData>
): FeatureFlagRoute[] => {
  const routes: FeatureFlagRoute[] = [];
  
  Object.entries(featureFlags).forEach(([key, flag]) => {
    // Only include page-type features
    if (flag.feature_type !== 'page') return;
    
    // Extract metadata with defaults
    const metadata = flag.metadata || {};
    const sidebarVisible = metadata.sidebar_visible !== false; // Default to true
    
    // Only include if explicitly visible (or no metadata = default visible)
    if (!sidebarVisible) return;
    
    routes.push({
      path: metadata.route_path || `/${key.replace(/_/g, '-')}`,
      label: flag.display_name,
      featureKey: key,
      icon: getIconForFeature(key), // Map feature key to icon component
      category: flag.category,
      order: metadata.display_order !== undefined ? metadata.display_order : 999,
      showInSidebar: true
    });
  });
  
  // Sort by order
  return routes.sort((a, b) => (a.order || 999) - (b.order || 999));
};

